import classNames from 'classnames';
import { Fragment } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import ShowMoreLinks from '@alltrails/layout/components/ShowMoreLinks/ShowMoreLinks';
import type { SeoLinkItem } from '@alltrails/types/SeoLinkItem';
import LinkSection from './LinkSection';
import styles from './SeoLinks.module.scss';
import { CURATED_HOMEPAGE_MAX_LINKS_PER_SECTION, DEFAULT_MAX_LINKS_PER_SECTION } from './seoLinksLimits';

const SEO_SECTION_STRINGS = defineMessages({
  TRAILS: {
    defaultMessage: 'Top {count} trails'
  },
  PARKS: {
    defaultMessage: 'Top {count} parks'
  },
  CITIES: {
    defaultMessage: 'Top {count} cities'
  },
  REGIONS: {
    defaultMessage: 'Top {count} regions'
  },
  STATES: {
    defaultMessage: 'Top {count} states'
  }
});

type SectionKey = 'REGIONS' | 'STATES' | 'TRAILS' | 'PARKS' | 'CITIES' | 'POIS';

export type SeoLinks = {
  key: SectionKey;
  data: SeoLinkItem[];
}[];

export type SeoLinksProps = {
  data: SeoLinks;
  className?: string;
  showAdditionalLinks?: boolean;
  isBot?: boolean;
};

// Desktop: https://www.figma.com/file/deyiqSQ1gfAajm667k20DU/SEO---Increase-internal-links?node-id=54%3A33765
// Mobile: https://www.figma.com/file/deyiqSQ1gfAajm667k20DU/SEO---Increase-internal-links?node-id=8%3A12152
const SeoLinks = ({ data, className, showAdditionalLinks = false, isBot = false }: SeoLinksProps) => {
  const intl = useIntl();
  const maxLinksPerSection = showAdditionalLinks ? CURATED_HOMEPAGE_MAX_LINKS_PER_SECTION : DEFAULT_MAX_LINKS_PER_SECTION;

  const sectionHeading = (sectionKey: SectionKey, linksLength: number) =>
    // POIS is only sent for en-US
    sectionKey === 'POIS' ? `Top ${linksLength} points of interest` : intl.formatMessage(SEO_SECTION_STRINGS[sectionKey], { count: linksLength });

  return (
    <>
      <div
        className={classNames(styles.container, className, {
          [styles.containerCurated]: showAdditionalLinks
        })}
      >
        <Typography component="h2" variant="heading500">
          <FormattedMessage defaultMessage="Adventure anywhere" />
        </Typography>
        <div className={styles.linkSection}>
          {data.map(section => {
            if (section.data.length === 0) return null;
            // The max number of links sent from the backend is 15, except for homepage curated lists where we send 50 links
            const links = section.data.slice(0, maxLinksPerSection);

            if (!showAdditionalLinks) {
              const heading = sectionHeading(section.key, links.length);
              return <LinkSection links={links} heading={heading} key={section.key} limit={maxLinksPerSection} />;
            }

            return (
              <Fragment key={section.key}>
                <ShowMoreLinks linkItems={links} collapsedLimit={DEFAULT_MAX_LINKS_PER_SECTION} isBot={isBot}>
                  {(visibleLinkItems, showMoreSlot) => {
                    const linkLayoutLimit =
                      visibleLinkItems.length > DEFAULT_MAX_LINKS_PER_SECTION
                        ? CURATED_HOMEPAGE_MAX_LINKS_PER_SECTION
                        : DEFAULT_MAX_LINKS_PER_SECTION;
                    const heading = sectionHeading(section.key, links.length);
                    return <LinkSection links={visibleLinkItems} heading={heading} limit={linkLayoutLimit} showMoreToggle={showMoreSlot} />;
                  }}
                </ShowMoreLinks>
              </Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default SeoLinks;
